import { useState } from 'react';
import { Navbar } from '@/components/layout/Navbar';
import { Footer } from '@/components/sections/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Mail, Phone, MapPin, Clock, Check, AlertCircle, Loader2, ArrowRight } from 'lucide-react';
import { useSEO } from '@/hooks/useSEO';

const BACKEND_URL = (() => {
  const url = process.env.REACT_APP_BACKEND_URL || '';
  if (!url) return '';
  return url.startsWith('http') ? url : 'https://' + url;
})();

const SERVICES = [
  { value: '', label: 'Select a service' },
  { value: 'defend',  label: 'Defend — Acodia Watch' },
  { value: 'learn',   label: 'Learn — Training' },
  { value: 'launch',  label: 'Launch — Career' },
  { value: 'reach',   label: 'Reach — Marketing' },
  { value: 'general', label: 'General enquiry' },
];

const inputStyle = {
  background: 'rgba(255,255,255,0.025)',
  border: '1px solid var(--b1)',
  color: 'var(--white)',
};

export default function ContactPage() {
  useSEO({
    title: 'Contact | Acodia — Grow. Defend. Be Found.',
    description: 'Get in touch with Acodia — we respond within one business day.',
    path: '/contact',
  });

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [service, setService] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!firstName.trim() || !lastName.trim()) { setError('Please enter your name'); return; }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) { setError('Please enter a valid email'); return; }
    if (!service) { setError('Please pick a service'); return; }
    if (!message.trim()) { setError('Please add a message'); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          email: email.trim().toLowerCase(),
          service,
          message: message.trim(),
          honeypot: '',
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const msg = typeof data?.detail === 'string'
          ? data.detail
          : (data?.detail?.error || `Could not send message (${res.status})`);
        throw new Error(msg);
      }
      setSuccess(data?.message || 'Thank you — we will be in touch within one business day.');
      setFirstName(''); setLastName(''); setEmail(''); setService(''); setMessage('');
    } catch (err) {
      setError(err?.message || 'Network error — please try again');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ background: 'var(--bg)', minHeight: '100vh', color: 'var(--white)' }}>
      <Navbar />
      <main style={{ paddingTop: 80, paddingBottom: 80 }}>
        <div
          className="mx-auto px-4 sm:px-6 lg:px-8"
          style={{ maxWidth: 600 }}
        >
          <h1
            style={{
              fontFamily: "'Syne', sans-serif", fontWeight: 700,
              fontSize: '2rem', color: 'var(--white)', textAlign: 'center',
              lineHeight: 1.2,
            }}
          >
            Get in Touch
          </h1>
          <p
            style={{
              fontFamily: 'DM Sans, sans-serif', fontSize: '0.9rem',
              color: 'var(--ts)', textAlign: 'center',
              marginTop: '0.65rem', lineHeight: 1.6,
            }}
          >
            Have a question or want to discuss your requirements? We respond within one business day.
          </p>

          {success ? (
            <div
              data-testid="contact-success"
              style={{
                background: 'rgba(74,222,128,0.06)',
                border: '1px solid rgba(74,222,128,0.35)',
                borderRadius: 12, padding: '1.5rem',
                marginTop: '2rem', textAlign: 'center',
              }}
            >
              <Check size={36} style={{ color: 'var(--ok)', margin: '0 auto 0.5rem' }} />
              <p style={{
                fontFamily: 'DM Sans, sans-serif', fontWeight: 600,
                fontSize: '1rem', color: 'var(--white)',
              }}>
                {success}
              </p>
            </div>
          ) : (
            <form
              data-testid="contact-form"
              onSubmit={handleSubmit}
              style={{
                marginTop: '2rem',
                background: 'var(--l1, rgba(255,255,255,0.04))',
                border: '1px solid var(--b1)',
                borderRadius: 12,
                padding: '1.5rem',
                display: 'flex', flexDirection: 'column', gap: '0.65rem',
              }}
            >
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
                <Input
                  data-testid="contact-first-name"
                  placeholder="First name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="rounded-[3px]"
                  style={{ ...inputStyle, height: 42 }}
                />
                <Input
                  data-testid="contact-last-name"
                  placeholder="Last name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className="rounded-[3px]"
                  style={{ ...inputStyle, height: 42 }}
                />
              </div>
              <Input
                data-testid="contact-email"
                type="email"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="rounded-[3px]"
                style={{ ...inputStyle, height: 42 }}
              />
              <select
                data-testid="contact-service"
                value={service}
                onChange={(e) => setService(e.target.value)}
                style={{
                  ...inputStyle, height: 42, borderRadius: 3,
                  padding: '0 0.75rem',
                  fontFamily: 'DM Sans, sans-serif', fontSize: '0.9rem',
                  appearance: 'none', WebkitAppearance: 'none',
                }}
              >
                {SERVICES.map((s) => (
                  <option key={s.value} value={s.value} style={{ background: '#0a0814', color: 'var(--white)' }}>
                    {s.label}
                  </option>
                ))}
              </select>
              <textarea
                data-testid="contact-message"
                placeholder="Tell us a little about what you need..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={4}
                style={{
                  ...inputStyle, borderRadius: 3,
                  padding: '0.65rem 0.75rem',
                  fontFamily: 'DM Sans, sans-serif', fontSize: '0.9rem',
                  resize: 'vertical', minHeight: 110,
                }}
              />
              {error && (
                <p
                  data-testid="contact-error"
                  style={{
                    fontFamily: 'DM Sans, sans-serif', fontSize: '0.82rem',
                    color: 'var(--danger)',
                    display: 'flex', alignItems: 'center', gap: '0.35rem',
                  }}
                >
                  <AlertCircle size={13} /> {error}
                </p>
              )}
              <Button
                type="submit"
                data-testid="contact-submit"
                disabled={submitting}
                className="btn-shimmer"
                style={{
                  background: 'var(--a)', color: '#fff',
                  height: 44, borderRadius: 3, marginTop: '0.35rem',
                  fontFamily: 'DM Sans, sans-serif', fontWeight: 500,
                  opacity: submitting ? 0.7 : 1,
                }}
              >
                {submitting ? (
                  <span className="inline-flex items-center" style={{ gap: '0.4rem' }}>
                    <Loader2 size={14} style={{ animation: 'scannerSpin 1s linear infinite' }} />
                    Sending...
                  </span>
                ) : (
                  <span className="inline-flex items-center" style={{ gap: '0.4rem' }}>
                    Send Message <ArrowRight size={14} />
                  </span>
                )}
              </Button>
            </form>
          )}

          <div
            data-testid="contact-details"
            style={{
              marginTop: '2rem',
              display: 'flex', flexDirection: 'column', gap: '0.75rem',
            }}
          >
            <ContactDetailRow icon={Mail} label="Email" value="connect@acodia.co.uk" href="mailto:connect@acodia.co.uk" />
            <ContactDetailRow icon={Phone} label="Phone" value="0330 122 1202" href="tel:+443301221202" />
            <ContactDetailRow icon={MapPin} label="Location" value="London, United Kingdom" />
            <ContactDetailRow icon={Clock} label="Response time" value="Within 1 business day" />
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}

function ContactDetailRow({ icon: Icon, label, value, href }) {
  const content = (
    <div
      style={{
        display: 'flex', alignItems: 'center', gap: '0.75rem',
        padding: '0.75rem 1rem',
        background: 'var(--l1, rgba(255,255,255,0.03))',
        border: '1px solid var(--b1)',
        borderRadius: 10,
      }}
    >
      <Icon size={16} style={{ color: 'var(--a)' }} />
      <div>
        <p style={{
          fontFamily: 'DM Sans, sans-serif', fontSize: '0.7rem',
          color: 'var(--ts)', letterSpacing: '0.05em', textTransform: 'uppercase',
        }}>
          {label}
        </p>
        <p style={{
          fontFamily: 'DM Sans, sans-serif', fontSize: '0.875rem',
          color: 'var(--white)',
        }}>
          {value}
        </p>
      </div>
    </div>
  );
  return href ? (
    <a href={href} style={{ textDecoration: 'none' }}>{content}</a>
  ) : content;
}
